// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { ActionType, IAction, raiseAction } from "actions";
import { IState } from "states/states";
import { IConnectorData, IOcppCharger, IOcppChargingSession } from "states/ocppstates";
import { getLocalDateTime, isSet, round } from "utils";
import Connector from "model/connector";

interface IChargerDataExtProps {
  isSessionView: boolean;
}

interface IChargerDataProps {
  chargerIndex: number | undefined;
  ocppCharger: IOcppCharger | undefined;
  nrConnectors: number;
  selectedConnector: number | undefined;
  lastOcppMessage: number | undefined;
  connectorData: (IConnectorData | undefined)[];
}

const mapStateToProps = (state: IState, ownProps: IChargerDataExtProps) => {
  let chargerId: number | undefined;
  let selectedConnector;
  if (ownProps.isSessionView) {
    // keep consistent with session loading in sagas:handleNavigation
    const session =
      state.ocpp.chargingSession.find((curItem) => curItem.id === state.router.transaction) ??
      (state.ocpp.pendingChargingSession[0] as IOcppChargingSession | undefined);
    chargerId = session?.fk_charger;
    selectedConnector = session?.connector;
  } else {
    chargerId = state.router.charger;
    selectedConnector = state.router.connector;
  }
  const chargerIndex = state.ocpp.charger.findIndex((curItem) => curItem.fk_charger === chargerId);
  const ocppCharger = chargerIndex >= 0 ? state.ocpp.charger[chargerIndex] : undefined;

  const props: IChargerDataProps = {
    chargerIndex,
    ocppCharger,
    nrConnectors:
      ocppCharger?.numberofconnectors ?? state.ocpp.connectorData[chargerIndex]?.length - 1 ?? 1,
    selectedConnector,
    lastOcppMessage: state.ocpp.lastOcppMessage[chargerIndex],
    connectorData: state.ocpp.connectorData[chargerIndex] ?? [],
  };
  return props;
};

interface IChargerDataDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChargerDataDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({});

class ChargerDataComponent extends React.Component<
  IChargerDataExtProps & IChargerDataProps & IChargerDataDispatch & WrappedComponentProps
> {
  public render() {
    return <div id="chargerdata">{this.renderStatus()}</div>;
  }

  private renderStatus() {
    // charger selection yields connector 1
    const connectorNumber = this.props.selectedConnector ? this.props.selectedConnector : 1;
    const connector = new Connector(
      this.props.connectorData,
      connectorNumber,
      this.props.lastOcppMessage,
    );
    // avoid showing charger status if we only have connector status (e.g. due to ocpp-gateway)
    const showChargerStatus = !connector.isOnline() || isSet(connector.stationStatus);
    // only show connector status if available (might be missing e.g. due to ocpp-gateway)
    const showConnectorStatus =
      connector.isOnline() && isSet(this.props.connectorData?.[connectorNumber]?.status);

    return (
      <table className="datatable">
        <tbody>
          {!this.props.isSessionView && showChargerStatus && (
            <tr>
              <th>
                <FormattedMessage id="Charger Status" />
              </th>
              <td>
                {connector.isOnline() ? (
                  // we may only have a connector status but no charger status (Dinitech NGRkick)
                  isSet(connector.stationStatus) && (
                    <span title={getLocalDateTime((connector.stationStatusTimestamp ?? 0) * 1000)}>
                      <FormattedMessage id={`ChargerStatus_${connector.stationStatus}`} />
                    </span>
                  )
                ) : (
                  <FormattedMessage id="Offline" />
                )}
              </td>
            </tr>
          )}
          {showConnectorStatus && (
            <tr>
              <th>
                <FormattedMessage id="Connector Status" />
              </th>
              <td
                title={
                  connector.statusTimestamp
                    ? getLocalDateTime(connector.statusTimestamp * 1000)
                    : undefined
                }
              >
                <FormattedMessage id={`ChargerStatus_${connector.status}`} />
              </td>
            </tr>
          )}
          {Boolean(connector.data?.energymeter) && (
            <tr>
              <th>
                <FormattedMessage id="Energymeter" />
              </th>
              <td>{`${round(connector.data!.energymeter, 0)} kWh`}</td>
            </tr>
          )}
          {isSet(connector.data?.voltage) && connector.data!.voltage.length > 0 && (
            <tr>
              <th>
                <FormattedMessage id="Voltage" />
              </th>
              <td>
                {`${connector.data!.voltage.map((curItem) => Math.round(curItem)).join(", ")} V`}
              </td>
            </tr>
          )}
          <tr>
            <th>
              <FormattedMessage id="Charging Power" />
            </th>
            <td>{isSet(connector.data?.power) && `${round(connector.data!.power, 1)} kW`}</td>
          </tr>
          <tr>
            <th>
              <FormattedMessage id="Charging Current" />
            </th>
            <td>
              {isSet(connector.data?.current) &&
                connector.data!.current.length > 0 &&
                `${connector
                  .data!.current.map((curValue) => {
                    return round(curValue, 1);
                  })
                  .join(", ")} A`}
            </td>
          </tr>
          {isSet(connector.data?.sessionEnergy) && (
            <tr>
              <th>
                <FormattedMessage id="Energy Session" />
              </th>
              <td>{`${round(connector.data?.sessionEnergy, 1)} kWh`}</td>
            </tr>
          )}
        </tbody>
      </table>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ChargerDataComponent));
